/**
 * The one error type a failed API call throws (both fetch choke points).
 *
 * `message` keeps the exact shape the app has always thrown —
 * `"<status> <statusText>: <body>"` — so the ~hundreds of `String(e)` call
 * sites keep rendering what they rendered. What is new is that a caller can
 * now BRANCH: on `status`, on the backend's `code`, and can show the
 * `requestId` the server logged the traceback under (backend/ops_integration
 * `_register_ops_exception_handler`), which is what turns "500 : Internal
 * Server Error" into something a person can act on.
 */
export type ApiErrorBody =
  | { detail?: unknown }
  | string
  | null

export class ApiError extends Error {
  readonly name = 'ApiError'
  readonly status: number
  readonly statusText: string
  readonly path: string
  /** Backend error code when the body carried one (`detail.error`), else null. */
  readonly code: string | null
  /** Server-side trace id for a 500 (`detail.trace_id` / `detail.request_id`). */
  readonly requestId: string | null
  /** The human-readable server message when the body carried one. */
  readonly serverMessage: string | null
  /** The parsed body, whatever shape it had. */
  readonly body: unknown

  constructor(path: string, status: number, statusText: string, bodyText: string) {
    super(`${status} ${statusText}: ${bodyText}`)
    this.status = status
    this.statusText = statusText
    this.path = path
    let body: unknown = bodyText
    try {
      body = JSON.parse(bodyText)
    } catch {
      // plain-text body — keep the string
    }
    this.body = body
    const detail = isRecord(body) ? body.detail : undefined
    if (isRecord(detail)) {
      this.code = typeof detail.error === 'string' ? detail.error
        : typeof detail.code === 'string' ? detail.code : null
      const rid = detail.trace_id ?? detail.request_id
      this.requestId = typeof rid === 'string' ? rid : null
      this.serverMessage = typeof detail.message === 'string' ? detail.message : null
    } else {
      this.code = null
      this.requestId = null
      this.serverMessage = typeof detail === 'string' ? detail : null
    }
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}
